import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiSettings } from '../../../core/config/api-settings';
import { AdminOverviewResult } from '../models/admin-overview.model';
import { AdminOrganizationRow, AdminOrganizationTotals } from '../models/admin-organization.model';
import { AdminCallRow, AdminCallTotals } from '../models/admin-call.model';
import { AdminTransactionRow, AdminTransactionTotals } from '../models/admin-transaction.model';
import { AdminUserRow, AdminUserTotals } from '../models/admin-user.model';
import { AdminPagination, AdminTotalsPage } from '../models/admin-pagination.model';
import { AdminOrganizationDetailOrg, AdminOrganizationDetailResult } from '../models/admin-organization-detail.model';

export interface AdminOverviewQuery {
  from?: string; // ISO 8601 server defaults to `to` minus 30 days
  to?: string;   // ISO 8601 server defaults to now
}

/** Shared by every list route's query args. `page`/`perPage` default to the
 *  server's own fallback (1 / 50) when omitted no need to repeat that here. */
export interface AdminListQuery {
  page?: number;
  perPage?: number;
}

export interface AdminOrganizationsQuery extends AdminListQuery {
  q?: string;
  exempt?: boolean;
  focus?: boolean;
}

export interface AdminCallsQuery extends AdminListQuery {
  /** Organization filter (slug, numeric id, or a name match) goes over
   *  the wire as `q`, not `organization` confirmed against the real API,
   *  not the written contract, which didn't list a Calls filter by this name. */
  q?: string;
  from?: string;
  to?: string;
  endpoint?: string;
  source?: string; // 'api' | 'playground'
  apiKeyId?: string; // the key's uid or its numeric id
  errorsOnly?: boolean;
}

export interface AdminTransactionsQuery extends AdminListQuery {
  /** Same `q`-not-`organization` wire name as AdminCallsQuery.q. */
  q?: string;
  from?: string;
  to?: string;
  kind?: string; // 'topup' | 'grant' | 'adjustment' | 'refund'
}

export interface AdminUsersQuery extends AdminListQuery {
  /** One shared text filter matches name/email and organization alike
   *  (confirmed against the real API) there's no separate `organization`
   *  param here, unlike what the written contract implied. */
  q?: string;
  superadmins?: boolean;
}

/** Every /api/v1/admin/* route lands here, all wired to the real,
 *  confirmed endpoints. */
@Injectable({ providedIn: 'root' })
export class AdminService {
  constructor(private http: HttpClient) {}

  getOverview(args: AdminOverviewQuery = {}): Observable<AdminOverviewResult> {
    const URL = `${ApiSettings.fullPath}admin/overview`;

    let params = new HttpParams();
    if (args.from) params = params.set('from', args.from);
    if (args.to) params = params.set('to', args.to);

    return this.http.get<any>(URL, { params }).pipe(
      map(res => new AdminOverviewResult(res)),
    );
  }

  getOrganizations(args: AdminOrganizationsQuery = {}): Observable<AdminTotalsPage<AdminOrganizationRow, AdminOrganizationTotals>> {
    const URL = `${ApiSettings.fullPath}admin/organizations`;

    let params = new HttpParams();
    if (args.q) params = params.set('q', args.q);
    // Boolean filters: send exactly "true" to switch one on, omit to leave
    // it off (per the contract, most other strings also count as "on").
    if (args.exempt) params = params.set('exempt', 'true');
    if (args.focus) params = params.set('focus', 'true');
    if (args.page) params = params.set('page', String(args.page));
    if (args.perPage) params = params.set('per_page', String(args.perPage));

    return this.http.get<any>(URL, { params }).pipe(
      map(res => ({
        rows: (res.organizations ?? []).map((r: any) => new AdminOrganizationRow(r)),
        pagination: new AdminPagination(res.pagination),
        // Absent until the backend adds it; the page hides its Totals card then.
        totals: res.totals ? new AdminOrganizationTotals(res.totals) : null,
      })),
    );
  }

  /** `idOrSlug` accepts either per the contract; an unknown value 404s,
   *  which the caller's catchError renders as "Organization not found". */
  getOrganization(idOrSlug: string): Observable<AdminOrganizationDetailResult> {
    const URL = `${ApiSettings.fullPath}admin/organizations/${idOrSlug}`;

    return this.http.get<any>(URL).pipe(
      map(res => new AdminOrganizationDetailResult(res)),
    );
  }

  /** The only write route in the panel. Accepts a plain boolean the
   *  backend also takes "true"/"false"/1/0, but there's no reason to send
   *  anything looser than what it means. Returns just the organization
   *  (detail shape, no members/api_keys/recent_calls) replace it in the
   *  caller's state rather than re-fetching the whole page. */
  updateOrganizationBilling(idOrSlug: string, billingExempt: boolean): Observable<AdminOrganizationDetailOrg> {
    const URL = `${ApiSettings.fullPath}admin/organizations/${idOrSlug}`;

    return this.http.patch<any>(URL, { organization: { billing_exempt: billingExempt } }).pipe(
      map(res => new AdminOrganizationDetailOrg(res.organization)),
    );
  }

  getCalls(args: AdminCallsQuery = {}): Observable<AdminTotalsPage<AdminCallRow, AdminCallTotals>> {
    const URL = `${ApiSettings.fullPath}admin/calls`;

    let params = new HttpParams();
    if (args.q) params = params.set('q', args.q);
    if (args.from) params = params.set('from', args.from);
    if (args.to) params = params.set('to', args.to);
    if (args.endpoint) params = params.set('endpoint', args.endpoint);
    if (args.source) params = params.set('source', args.source);
    if (args.apiKeyId) params = params.set('api_key_id', args.apiKeyId);
    if (args.errorsOnly) params = params.set('errors_only', 'true');
    if (args.page) params = params.set('page', String(args.page));
    if (args.perPage) params = params.set('per_page', String(args.perPage));

    return this.http.get<any>(URL, { params }).pipe(
      map(res => ({
        rows: (res.calls ?? []).map((r: any) => new AdminCallRow(r)),
        pagination: new AdminPagination(res.pagination),
        // Absent until the backend adds it; the page hides its Totals card then.
        totals: res.totals ? new AdminCallTotals(res.totals) : null,
      })),
    );
  }

  /** totals cover the whole filtered list, not just the page (one call is
   *  enough to show "In"/"Out" above the table it doesn't need a
   *  separate, unfiltered request). */
  getTransactions(args: AdminTransactionsQuery = {}): Observable<{ rows: AdminTransactionRow[]; totals: AdminTransactionTotals; pagination: AdminPagination }> {
    const URL = `${ApiSettings.fullPath}admin/transactions`;

    let params = new HttpParams();
    if (args.q) params = params.set('q', args.q);
    if (args.from) params = params.set('from', args.from);
    if (args.to) params = params.set('to', args.to);
    if (args.kind) params = params.set('kind', args.kind);
    if (args.page) params = params.set('page', String(args.page));
    if (args.perPage) params = params.set('per_page', String(args.perPage));

    return this.http.get<any>(URL, { params }).pipe(
      map(res => ({
        rows: (res.transactions ?? []).map((r: any) => new AdminTransactionRow(r)),
        totals: new AdminTransactionTotals(res.totals),
        pagination: new AdminPagination(res.pagination),
      })),
    );
  }

  getUsers(args: AdminUsersQuery = {}): Observable<AdminTotalsPage<AdminUserRow, AdminUserTotals>> {
    const URL = `${ApiSettings.fullPath}admin/users`;

    let params = new HttpParams();
    if (args.q) params = params.set('q', args.q);
    if (args.superadmins) params = params.set('superadmins', 'true');
    if (args.page) params = params.set('page', String(args.page));
    if (args.perPage) params = params.set('per_page', String(args.perPage));

    return this.http.get<any>(URL, { params }).pipe(
      map(res => ({
        rows: (res.users ?? []).map((r: any) => new AdminUserRow(r)),
        pagination: new AdminPagination(res.pagination),
        // Absent until the backend adds it; the page hides its Totals card then.
        totals: res.totals ? new AdminUserTotals(res.totals) : null,
      })),
    );
  }
}
